<script lang="ts" setup>
import {ITableItem} from "@/types/table";
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {useRoute} from "vue-router";
import router from "@/router";

interface IChildList {
  list: ITableItem[]
}

const props = defineProps<IChildList>();

const emit = defineEmits<{
  (e: 'scroll', type: 'top' | 'bottom'): void
}>()

const listRef = ref<HTMLElement | null>(null);
const itemRefs = ref<HTMLElement[]>([]);
const hasScrolledToActive = ref(false);

const route = useRoute();
const activeItemId = computed(() => route.params.id);

const setItemRef = (el: Element | null, index: number) => {
  if (el) {
    itemRefs.value[index] = el as HTMLElement;
  }
};

const scrollActiveToTop = async () => {
  if (hasScrolledToActive.value) return;

  await nextTick();

  const container = listRef.value;
  const activeIndex = props.list.findIndex(
      item => String(item.chat_id) === String(activeItemId.value)
  );

  if (activeIndex === -1) return;

  const activeEl = itemRefs.value[activeIndex];
  if (!container || !activeEl) return;

  container.scrollTo({
    top: activeEl.offsetTop,
    behavior: "smooth",
  });

  hasScrolledToActive.value = true;
};

const selectNewItem = (item: any) => {
  router.push({path: `/chat/${item.chat_id}`, query: {chat: item.account_id}});
};

const handleScroll = () => {
  const el = listRef.value
  if (!el) return

  const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight

  if (distanceFromBottom < 200) {
    emit("scroll", 'bottom');
  }

  if (el.scrollTop < 200) {
    emit("scroll", 'top');
  }
}

onMounted(async () => {
  if (listRef.value) {
    listRef.value.addEventListener('scroll', handleScroll)
  }
})

onBeforeUnmount(() => {
  if (listRef.value) {
    listRef.value.removeEventListener('scroll', handleScroll)
  }
})

watch(
    activeItemId,
    () => {
      hasScrolledToActive.value = false;
      scrollActiveToTop();
    },
    {immediate: true}
);

watch(
    () => props.list,
    () => {
      itemRefs.value = [];
      scrollActiveToTop();
    },
    {deep: true}
);
</script>

<template>
  <div class="c-cu-child-list">
    <div class="list" ref="listRef">
      <div
          v-for="(item, index) in list"
          :key="item.id"
          :ref="el => setItemRef(el, index)"
          class="list__item"
          :class="{ active: String(item.chat_id) === String(activeItemId) }"
          @click="selectNewItem(item)"
      >
        <div class="list__item--header">
          {{ item.title }}
        </div>

        <div class="list__item--body">
          <div class="account">
            {{ item.session_name }}
          </div>
          <div class="stats">
            <div class="all">
              <span>{{ item.messages.total }}</span>
            </div>
            <div class="error">
              <span>{{ item.errors.total }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.c-cu-child-list {
  .list {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    height: calc(100vh - 149px);
    overflow: auto;

    &__item {
      padding: 8px 12px 12px 12px;
      border-radius: 16px;
      border: 1px solid var(--color-border);
      display: flex;
      flex-direction: column;
      background-color: var(--color-bg-tertiary);
      cursor: pointer;

      &--header {
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
        color: var(--color-label);
        letter-spacing: 0;
        background: transparent;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      &--body {
        display: flex;
        justify-content: space-between;
        align-items: center;
        background: transparent;

        .account {
          color: var(--color-secondary-label);
          background: transparent;
        }

        .stats {
          display: flex;
          border: 1px solid var(--color-border);
          background: var(--color-background);
          border-radius: 12px;
          overflow: hidden;

          .all, .error {
            display: flex;
            justify-content: center;
            background: transparent;
            width: 50%;
            color: var(--color-secondary-label);

            span {
              font-weight: 500;
              padding: 6px 12px;
              font-size: 12px;
              line-height: 14px;
              background: transparent;
              color: var(--color-label);
            }
          }

          .all {
            border-right: 1px solid var(--color-border);
          }

          .error {
            span {
              color: var(--color-red);
            }
          }
        }
      }

      &.active {
        border: 2px solid var(--color-label);
        padding: 7px 11px 11px 11px;
        background-color: var(--color-bg-tertiary);
      }
    }
  }
}
</style>